<script setup lang="ts">
/*
* Компонент NewTreeComponent - модальное окно выбора местоположения в иерархической структуре больницы
*
* Основные функции:
* - Отображение древовидной структуры помещений больницы с поддержкой поиска
* - Выбор корпуса, этажа или помещения через интерактивное дерево
* - Восстановление и синхронизация выбранного состояния из внешних props
* - Оптимизация производительности через виртуальное отображение узлов
*
* Особенности реализации:
* - Дебаунс поиска для оптимизации производительности при вводе
* - Динамическое раскрытие узлов дерева на основе поискового запроса
* - Подсветка совпадений в результатах поиска
* - Поддержка двойного клика для быстрого выбора нелистовых узлов
* - Интеграция с глобальными хранилищами для данных и уведомлений
*/

// Импорты Vue
import { computed, onMounted, ref, watch } from 'vue'
import { useDisplay } from 'vuetify/framework'
import { VIconBtn } from 'vuetify/labs/VIconBtn'

// Импорты хранилищ
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

// Импорты типов
import type { ITreeNode } from '@/modules/apiStore/types/ITreeNode'
import AnimatedLogoLoader from '@/components/createdUi/AnimatedLogoLoader.vue'

const props = defineProps({
  placeholder: { type: String, default: '' },
  searchPlaceholder: { type: String, default: '' },
  label: { type: String, default: '' },
  isLabelRequired: { type: Boolean, default: false },
  error: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  selected: {
    type: Object as PropType<{
      value: { id: string; name?: string } | null
    }>,
    default: () => ({ value: null }),
  },
  treeData: {
    type: Array as PropType<ITreeNode[]>,
    required: true,
  },
  /*
   * Если передан — непустой запрос уходит на сервер, в списке ответ как есть.
   * Без пропа поиск остаётся локальным по treeData.
   */
  searchTree: {
    type: Function as PropType<(query: string) => Promise<ITreeNode[]>>,
    default: undefined,
  },
})

const emit = defineEmits<{
  'update:selected': [value: { id: string } | null]
}>()

// Инициализация хранилищ
const feedbackStore = useFeedbackStore()

const { xs } = useDisplay()

// Реактивные состояния компонента
// Флаг видимости модального окна
const showDropdown = ref(false)

// Массив ключей выбранных узлов
const selectedKeys = ref<string[]>([])

// Массив ключей раскрытых узлов
const expandedKeys = ref<string[]>([])

// Строковое представление выбранных элементов
const selectedItemsString = ref<string>('')

// Текст поискового запроса
const search = ref('')

// Дебаунсированный текст поиска
const searchDebounced = refDebounced(search, 300)

const serverTree = ref<ITreeNode[] | null>(null)
const searchLoading = ref(false)
let searchSeq = 0

const useServerSearch = computed(() => Boolean(props.searchTree) && searchDebounced.value.trim().length > 0)

const activeTree = computed(() => useServerSearch.value ? (serverTree.value ?? []) : props.treeData)

// Флаг чувствительности к регистру при поиске
const caseSensitive = ref(false)

// Флаг предотвращения рекурсии при восстановлении выбора и защиты от перезаписи свежего выбора
let isRestoring = false

/**
 * Вычисляемое свойство для видимых узлов дерева
 *
 * Назначение:
 * - Фильтрация и подготовка узлов для отображения с учетом поиска и раскрытия
 * - Оптимизация рендеринга через отсечение невидимых узлов
 *
 * Возвращает:
 * - ITreeNode[]: массив узлов для отображения с дополнительными метаданными
 */
const visibleNodes = computed(() => {
  const term = useServerSearch.value ? '' : searchDebounced.value
  return getVisibleNodes(activeTree.value, expandedKeys.value, term)
})

function collectExpandableKeys(nodes: ITreeNode[]): string[] {
  const keys: string[] = []
  const walk = (list: ITreeNode[]) => {
    for (const node of list) {
      if (node.children?.length) {
        keys.push(node.key)
        walk(node.children)
      }
    }
  }
  walk(nodes)
  return keys
}

watch(searchDebounced, async query => {
  if (!props.searchTree)
    return

  const trimmed = query.trim()
  if (!trimmed) {
    searchSeq += 1
    serverTree.value = null
    searchLoading.value = false
    expandTreeOnInit()
    return
  }

  const seq = ++searchSeq
  searchLoading.value = true
  try {
    const tree = await props.searchTree(trimmed)
    if (seq !== searchSeq)
      return
    serverTree.value = tree
    expandedKeys.value = collectExpandableKeys(tree)
  }
  catch {
    if (seq !== searchSeq)
      return
    serverTree.value = []
    feedbackStore.showToast({
      type: 'error',
      message: 'Не удалось выполнить поиск',
      time: 5000,
    })
  }
  finally {
    if (seq === searchSeq)
      searchLoading.value = false
  }
})

/**
 * Рекурсивная функция получения видимых узлов
 *
 * Назначение:
 * - Определение какие узлы должны быть видны с учетом поиска, раскрытия и совпадений в родительских ветках
 * - Добавление метаданных для рендеринга (глубина, является ли листом)
 *
 * Входные данные:
 * - nodes: ITreeNode[] - массив узлов для обработки
 * - expandedKeys: string[] - ключи раскрытых узлов
 * - searchTerm: string - поисковый запрос
 * - depth: number - текущая глубина в дереве
 * - isParentMatch: boolean - флаг, указывающий, совпал ли родительский узел с поисковым запросом
 *
 * Возвращает:
 * - any[]: массив видимых узлов с метаданными
 */
function getVisibleNodes(
  nodes: ITreeNode[],
  expandedKeys: string[],
  searchTerm = '',
  depth = 0,
  isParentMatch = false,
): any[] {
  const visible: any[] = []

  nodes.forEach(node => {
    const isSelfMatch = node.label.toLowerCase().includes(searchTerm.toLowerCase())
    const isVisible = !searchTerm
      || isParentMatch
      || isSelfMatch
      || isChildMatchingSearch(node, searchTerm)

    if (isVisible) {
      visible.push({
        ...node,
        depth,
        isLeaf: !node.children || node.children.length === 0,
      })

      const shouldShowChildren = expandedKeys.includes(node.key) || searchTerm
      if (shouldShowChildren && node.children) {
        const nextParentMatch = isParentMatch || isSelfMatch
        visible.push(...getVisibleNodes(node.children, expandedKeys, searchTerm, depth + 1, nextParentMatch))
      }
    }
  })

  return visible
}

/**
 * Проверка соответствия дочерних узлов поисковому запросу
 *
 * Назначение:
 * - Рекурсивная проверка наличия совпадений в потомках узла
 * - Обеспечивает показ родительских узлов при совпадении в детях
 *
 * Входные данные:
 * - node: ITreeNode - узел для проверки
 * - searchTerm: string - поисковый запрос
 *
 * Возвращает:
 * - boolean: true если хотя бы один потомок соответствует поиску
 */
function isChildMatchingSearch(node: ITreeNode, searchTerm: string): boolean {
  if (!node.children) return false
  return node.children.some(child =>
    child.label.toLowerCase().includes(searchTerm.toLowerCase())
    || isChildMatchingSearch(child, searchTerm),
  )
}

/**
 * Переключение состояния раскрытия узла
 *
 * Назначение:
 * - Добавление/удаление ключа узла из массива раскрытых узлов
 * - Управление видимостью дочерних элементов в дереве
 *
 * Входные данные:
 * - node: ITreeNode - узел для переключения
 */
function toggleNode(node: ITreeNode) {
  const isExpanded = expandedKeys.value.includes(node.key)
  if (isExpanded) {
    expandedKeys.value = expandedKeys.value.filter(key => key !== node.key)
  } else {
    expandedKeys.value = [...expandedKeys.value, node.key]
  }
}

/**
 * Обработчик клика по узлу дерева
 *
 * Назначение:
 * - Выбирает узел при клике
 * - Применяет выбор и закрывает модальное окно
 * - Для нелистовых узлов дополнительно переключает состояние раскрытия
 *
 * Порядок работы:
 * 1. Устанавливает выбранный узел
 * 2. Получает и отображает полный путь до выбранного узла
 * 3. Применяет выбор (emit события, закрытие модалки)
 * 4. Если узел не является листом - переключает его раскрытие
 *
 * Параметры:
 * - node: ITreeNode — узел дерева, по которому был произведён клик
 *
 * Побочные эффекты:
 * - Модифицирует selectedKeys, selectedItemsString
 * - Вызывает applySelection() для применения выбора
 * - Может изменить состояние разворота узла через toggleNode()
 */
function handleNodeSelect(node: ITreeNode) {
  // === выбор одинарным кликом ===
  selectedKeys.value = [node.key]
  // ПОЛУЧАЕМ ПОЛНЫЙ ПУТЬ ОТ КОРНЯ ДО ВЫБРАННОГО УЗЛА
  const fullPath = getFullNodePath(activeTree.value.length ? activeTree.value : props.treeData, node.key)
  selectedItemsString.value = fullPath.join(', ')

  // Применяем выбор сразу при клике
  applySelection()

  // Если узел не является листом - также раскрываем/скрываем его
  if (!node.isLeaf) {
    toggleNode(node)
  }
}

/**
 * Эмитинг выбранного местоположения в родительский компонент
 *
 * Назначение:
 * - Передача данных о выбранной локации в родительский компонент
 * - Упрощенная логика: передается один объект с ID вместо трех отдельных частей
 * - Обеспечивает согласованность данных между компонентами
 *
 * Изменения:
 * - Ранее: разбор сложного ключа на отдельные части (блок, этаж, помещение)
 * - Теперь: передача простого объекта {id: string} с ID выбранной локации
 * - Упрощение API компонента и поддержка произвольной структуры локаций
 *
 * Алгоритм:
 * 1. Получение ID последнего выбранного узла
 * 2. Эмитинг события с объектом {id: string} или null при сбросе
 * 3. Обеспечение типобезопасности через TypeScript
 *
 * Использование:
 * - Родительский компонент получает единый объект локации
 * - Поддержка любой глубины вложенности и типов локаций
 * - Упрощенная обработка в родительских компонентах
 */
function emitSelectedParts() {
  const lastKey = selectedKeys.value[selectedKeys.value.length - 1]

  if (lastKey) {
    // Теперь передаем просто объект с id выбранной локации
    emit('update:selected', { id: lastKey })
  } else {
    emit('update:selected', null)
  }
}

/**
 * Применение выбора и закрытие модального окна
 *
 * Назначение:
 * - Валидация выбора (должен быть выбран хотя бы один элемент)
 * - Эмитинг данных о выборе
 * - Закрытие модального окна
 * - Включение защиты от перезаписи выбора (isRestoring = true)
 */
function applySelection() {
  if (selectedKeys.value.length === 0) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Пожалуйста, выберите местоположение',
      time: 10000,
    })
    return
  }

  isRestoring = true
  emitSelectedParts()
  showDropdown.value = false

  setTimeout(() => {
    isRestoring = false
  }, 100)
}

/**
 * Сброс поискового запроса
 *
 * Назначение:
 * - Очистка поля поиска и сброс связанных состояний
 * - Остановка таймера дебаунса
 */
const resetSearch = () => {
  search.value = ''
  caseSensitive.value = false
}

/**
 * Полная очистка выбранных элементов
 *
 * Назначение:
 * - Сброс всех выбранных состояний
 * - Эмитинг событий сброса в родительский компонент
 * - Закрытие модального окна
 */
const clearSelection = () => {
  resetSearch()
  selectedKeys.value = []
  selectedItemsString.value = ''
  emit('update:selected', null)
  showDropdown.value = false
}

/**
 * Функция подсветки совпадений в тексте
 *
 * Назначение:
 * - Обертывание совпадающих частей текста в HTML-теги для визуального выделения
 * - Экранирование специальных символов в поисковом запросе
 *
 * Входные данные:
 * - label: string - исходный текст
 * - searchTerm: string - поисковый запрос
 *
 * Возвращает:
 * - string: HTML-строка с подсвеченными совпадениями
 */
const highlightMatch = (label: string, searchTerm: string) => {
  if (!searchTerm) return label
  const safe = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`(${safe})`, 'gi')
  return (label ?? '').replace(re, '<mark class="search-highlight">$1</mark>')
}

/**
 * Инициализационное раскрытие узлов дерева
 *
 * Назначение:
 * - Автоматическое раскрытие корневых узлов при первом открытии
 * - Обеспечение видимости основной структуры дерева
 */
function expandTreeOnInit() {
  const newExpandedKeys: string[] = []
  for (const data of props.treeData) {
    newExpandedKeys.push(data.key)
  }
  expandedKeys.value = newExpandedKeys
}

/**
 * Восстановление выбора из внешних props
 *
 * Назначение:
 * - Синхронизация внутреннего состояния с переданными значениями локации
 * - Корректное отображение ранее выбранного местоположения при открытии модалки
 * - Защита от перезаписи свежесделанного выбора через флаг isRestoring
 *
 * Изменения:
 * - Ранее: сложная логика поиска больницы по блоку и построение составного ключа
 * - Теперь: прямое использование ID локации из props для выделения узла
 * - Отображение полного пути вместо только названия конечной локации
 *
 * Алгоритм:
 * 1. Проверка флага isRestoring (пропускаем если выбор только что сделан)
 * 2. Получение ID локации из props.selected.location
 * 3. Установка выделения и раскрытие пути до выбранной локации
 * 4. Формирование полного пути для отображения в UI
 *
 * Особенности:
 * - Поддержка произвольной вложенности локаций через рекурсивный поиск пути
 * - Автоматическое раскрытие дерева до выбранного узла
 * - Защита от циклических обновлений через флаг isRestoring
 */
function restoreSelectionFromProp() {
  if (isRestoring) return
  isRestoring = true

  try {
    const selected = props.selected || { id: null }
    const { value } = selected

    // если ничего нет — сбросим выделение
    if (!value?.id) {
      selectedKeys.value = []
      selectedItemsString.value = ''
      expandTreeOnInit()
      return
    }

    // Теперь просто используем ID локации как ключ
    selectedKeys.value = [value.id]

    // Раскрываем путь до выбранной локации
    expandedKeys.value = findPathToNode(props.treeData, value.id)

    // ПОЛУЧАЕМ ПОЛНЫЙ ПУТЬ ДЛЯ ОТОБРАЖЕНИЯ
    const fullPath = getFullNodePath(props.treeData, value.id)

    // Узла нет в дереве пользователя (например, учебная запись онбординга) — показываем имя из самого значения
    selectedItemsString.value = fullPath.length ? fullPath.join(', ') : (value.name ?? '')
  } finally {
    isRestoring = false
  }
}

/**
 * Получение полного иерархического пути от корня до целевого узла
 *
 * Назначение:
 * - Формирует массив названий локаций от верхнего уровня до выбранной
 * - Используется для отображения полного пути в UI вместо только названия конечной локации
 * - Обеспечивает понятное отображение контекста выбранного местоположения
 *
 * Особенности:
 * - Работает с произвольной глубиной вложенности локаций
 * - Возвращает массив названий для последовательного отображения
 * - Рекурсивно обходит дерево до нахождения целевого узла
 *
 * Алгоритм:
 * 1. Рекурсивный обход дерева с накоплением пути
 * 2. При нахождении целевого узла сохраняет накопленный путь
 * 3. Возвращает массив названий локаций в порядке от корня к листу
 *
 * Входные данные:
 * - nodes: ITreeNode[] - корневые узлы дерева для поиска
 * - targetKey: string - ID целевой локации
 *
 * Возвращает:
 * - string[] - массив названий локаций от корня до целевого узла
 */
function getFullNodePath(nodes: ITreeNode[], targetKey: string): string[] {
  const path: string[] = []

  function findPath(nodeList: ITreeNode[], currentPath: string[] = []): boolean {
    for (const node of nodeList) {
      const newPath = [...currentPath, node.label]
      if (node.key === targetKey) {
        path.push(...newPath)
        return true
      }
      if (node.children && node.children.length > 0) {
        if (findPath(node.children, newPath)) {
          return true
        }
      }
    }
    return false
  }

  findPath(nodes)
  return path
}

// Вспомогательная функция для поиска пути к узлу
function findPathToNode(nodes: ITreeNode[], targetKey: string): string[] {
  for (const node of nodes) {
    if (node.key === targetKey) {
      return [node.key]
    }
    if (node.children) {
      const childPath = findPathToNode(node.children, targetKey)
      if (childPath.length > 0) {
        return [node.key, ...childPath]
      }
    }
  }
  return []
}

/**
 * Вотчер для дебаунса поискового запроса
 *
 * Назначение:
 * - Откладывает обработку поиска на 300ms для оптимизации производительности
 * - Предотвращает частые перерисовки при быстром вводе текста
 */
/**
 * Вотчер для синхронизации с внешними изменениями props
 *
 * Назначение:
 * - Реакция на изменения selected prop извне
 * - Восстановление состояния при программном изменении выбора
 */
watch(() => props.selected, () => {
  // Восстанавливаем только при реальном изменении (deep compare нужен)
  restoreSelectionFromProp()
}, { immediate: true, deep: true })

/**
 * Вотчер для реакции на загрузку данных дерева
 *
 * Назначение:
 * - Восстановление выбора при асинхронной загрузке структуры больницы
 * - Обеспечение корректного отображения при delayed data loading
 */
watch(() => props.treeData, newTree => {
  if (!newTree?.length) return
  // Когда дерево появилось — попросим модалку восстановить выделение.
  restoreSelectionFromProp()
}, { immediate: true, deep: true })

/**
 * Хук монтирования компонента
 *
 * Выполняет:
 * - Инициализационное раскрытие дерева
 * - Загрузку скелетона больницы если необходимо
 */
onMounted(async () => {
  if (props.treeData.length) {
    expandTreeOnInit()
  }
})
</script>

<template>
  <VMenu
    v-model="showDropdown"
    :close-on-content-click="false"
    :eager="false"
    :max-height="500"
    location="bottom"
    transition="scale-transition"
    content-class="entity-form-menu tree-select-menu"
  >
    <template #activator="{ props: menuProps }">
      <VTextarea
        v-bind="menuProps"
        variant="outlined"
        density="comfortable"
        :placeholder="placeholder"
        persistent-placeholder
        readonly
        :model-value="selectedItemsString"
        :append-inner-icon="showDropdown ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
        :eager="false"
        class="invisible-readonly"
        clearable
        :error="error"
        :disabled="disabled"
        rows="1"
        auto-grow
        no-resize
        @click:clear="clearSelection"
      >
        <template #label>
          <span> {{ props.label }}<sup v-if="isLabelRequired" class="required-label">*</sup></span>
        </template>
      </VTextarea>
    </template>

    <VCard
      v-if="showDropdown"
      class="d-flex pa-4 flex-column gap-2"
    >
      <VSheet>
        <VTextField
          v-model="search"
          variant="outlined"
          clearable
          rounded="pill"
          class="search-field"
          :placeholder="searchPlaceholder"
          :append-inner-icon="search ? undefined : 'ri-search-line'"
          @click:clear="resetSearch"
        />
      </VSheet>

      <div v-if="searchLoading" class="tree-search-loader d-flex align-center justify-center">
        <AnimatedLogoLoader />
      </div>
      <div v-else-if="useServerSearch && !visibleNodes.length" class="text-body-2 text-medium-emphasis">
        Ничего не найдено
      </div>
      <VList v-else class="tree-view-content custom-tree">
        <div
          v-for="item in visibleNodes"
          :key="item.key"
          class="tree-item"
          :class="{
            'tree-item--selected': selectedKeys.includes(item.key),
            'tree-item--leaf': item.isLeaf,
          }"
          :style="{ paddingLeft: `${item.depth * 34 + 8}px` }"
          @click="handleNodeSelect(item)"
        >
          <VIconBtn
            v-if="!item.isLeaf"
            icon="ri-play-fill"
            class="expand-btn"
            variant="text"
            :class="{ expanded: expandedKeys.includes(item.key) }"
            @click.stop="toggleNode(item)"
          />
          <div v-else class="node-leaf-spacer" />
          <span
            class="text-body-1 text-high-emphasis tree-item__label"
            v-html="highlightMatch(item.label, searchDebounced)"
          />
        </div>
      </VList>
    </VCard>
  </VMenu>
</template>

<style scoped lang="scss">
:global(.tree-select-menu) {
  max-width: 0 !important;
}

.tree-view-content {
  max-height: 400px;
  overflow-y: auto;
}

.tree-search-loader {
  min-height: 160px;
}

:deep(.search-highlight) {
  background-color: rgb(var(--v-theme-primary-500), var(--v-activated-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity)) !important;
  padding: 2px 0;
  border-radius: 2px;
}
</style>
